// biome-ignore-all lint/suspicious/noArrayIndexKey: importLog is fully replaced on each load (not mid-mutated), so index keys are safe here.
// biome-ignore-all lint/a11y/noLabelWithoutControl: this file is the internal Zephyr-staff admin console (user management, shelter management, lookups, templates) — not the case-worker request flow. Caption-style <label> elements above grouped controls are intentional; full htmlFor/id wiring across ~70 fields would be high-churn for low real-world benefit on a staff-only surface.
//
// =============================================================================
// AdminPages.jsx
// =============================================================================
// Staff/admin-only screens: bulk import, user + shelter management, lookup
// editors (standard costs, templates, banners), and the audit log viewer.
// Each top-level export is a separate full-page screen rendered by portal.jsx
// when the user navigates from the Header's admin menu.
//
// TABLE OF CONTENTS
// ─────────────────
//   Page-level screens
//     ImportPage             (line   ~28) — bulk-import shelters from a CSV
//     UserAdmin              (line  ~511) — manage users + shelters (CRUD, password reset, MFA reset)
//     OnboardingEmailsAdmin  (line ~4467) — manage the onboarding-email template
//     StandardCostsAdmin     (line ~4637) — edit the device + stationery cost catalogue
//     HelpContentAdmin       (line ~4958) — edit the contextual help_content texts
//     NextStepsAdmin         (line ~5132) — edit next-steps action-sheet templates (JSONB blob)
//     TemplatesAdmin         (line ~5417) — edit comment templates (rendered by TemplatePickerModal)
//     BannersAdmin           (line ~5947) — manage dashboard banners
//     AuditLogPage           (line ~6569) — paginated viewer for the audit_logs table
//   Embedded / shared
//     ActiveBanners          (line ~6491) — read-only banners panel imported into dashboards
//
// DATA FLOW
// ─────────
//   - Each screen reads its own table(s) directly via Supabase: profiles,
//     shelters, user_shelters, delivery_addresses, portal_config, banners,
//     comment_templates, help_content, audit_logs.
//   - User CRUD that touches auth.users (create / set email / reset password)
//     routes through the `admin-users` Edge Function (service-role client).
//   - logAudit(...) records every state-changing action.
//
// SEE ALSO
// ────────
//   supabase/functions/admin-users/  — Edge Function backing user CRUD
//   src/utils/auditLog.js             — logAudit helper used throughout
//   src/services/approvals.js         — NEXT_STEPS_DEFAULTS seed used by NextStepsAdmin
// =============================================================================

import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isSuperUser } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import {
  listActiveByShelterId,
  listShelterIdsWithPendingAddresses,
} from "../../data/deliveryAddresses";
import {
  insertLookupValue,
  listLookupOptions,
  listLookupValues,
  loadCustomerTypeCategories,
} from "../../data/lookups";
import { listShelterProfiles, listStaffProfiles } from "../../data/profiles";
import { countInflightRequestsByShelterId } from "../../data/requests";
import { listAllShelters } from "../../data/shelters";
import { listAllUserShelters } from "../../data/userShelters";
import { usePageSize } from "../../hooks/usePageSize";
import { useSisterIndex } from "../../hooks/useSisterIndex";
import { useUserAdminHandlers } from "../../hooks/useUserAdminHandlers";
import {
  assessUserDeactivation,
  attachedUsersByShelter,
  cascadeWorkersForShelter,
  isTestShelter,
  NO_SALES_GROUP,
  salesGroupsFromShelters,
  sharedWorkersForShelter,
  shelterListCsvTable,
  shelterMatchesQuery,
  shelterPassesDeactivatedFilter,
  shelterPassesListFilters,
  sortShelters,
  statesFromShelters,
} from "../../services/accountLifecycle";
import { hasSisters } from "../../services/sisterGroups";
import { downloadCsv } from "../../utils/csv";
import { ROLES } from "../../utils/enums";
import { AddLookupModal } from "./AddLookupModal.jsx";
import { DeactivateUserConfirmModal } from "./DeactivateUserConfirmModal.jsx";
import { NewShelterWizard } from "./NewShelterWizard.jsx";
import { NewUserModal } from "./NewUserModal.jsx";
import { PrimaryContactCheckModal } from "./PrimaryContactCheckModal.jsx";
import { ShelterDeactivateConfirmModal } from "./ShelterDeactivateConfirmModal.jsx";
import { ShelterEditor } from "./ShelterEditor.jsx";
import { StaffCommsModal } from "./StaffCommsModal.jsx";
import { UserAdminShelterList } from "./UserAdminShelterList.jsx";
import { UserAdminStaffList } from "./UserAdminStaffList.jsx";
import { UserAdminTabsBar } from "./UserAdminTabsBar.jsx";

const ROLE_LABELS = { staff: "Responder", picker: "Picker", admin: "Admin", combined: "Combined" };
function _roleLabel(r) {
  return ROLE_LABELS[r] || r;
}

// ── USER MANAGEMENT PAGE ────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// UserAdmin — user + shelter management console (the biggest admin screen)
// ─────────────────────────────────────────────────────────────────────────────
// Renders: tabbed view with three lists — users, shelters, and delivery
//          addresses per shelter. Supports CRUD across all three plus
//          password reset, MFA reset, role changes, and shelter mappings
//          (the `user_shelters` junction table).
// Session:
//   user - current admin user (audit + self-edit guards), read from useSession
// Props (5 July 2026, shelter-admin redesign — Manage split; Caseworkers tab
// RETIRED in Phase 3, caseworkers are managed on the shelter detail's Users tab):
//   sections - which section tabs this instance shows. The default instance
//              (Manage → Shelters, route /users) is shelters-only;
//              ZephyrStaffAdmin below renders ["staff"] only.
//   title    - the page H1 ("Shelters" / "Zephyr Staff").
// Notes: writes that touch auth.users (create / update email / reset password)
//        go through the admin-users Edge Function. Direct table writes for
//        everything else.
function UserAdmin({ sections = ["shelters"], title = "DFV Services" }) {
  // `user` now comes from SessionContext rather than a prop (Phase 3 refactor).
  const { user } = useSession();
  const navigate = useNavigate();
  // /users/:shelterCode → the full-screen tabbed ShelterDetail replaces the
  // list screen (shelter-admin redesign Phase 1). Undefined on /users + /staff.
  const { shelterCode } = useParams();
  // shelters = rows from shelters table (org records)
  // caseworkers = profiles WHERE role = 'shelter'
  // staffRows = profiles WHERE role IN (staff, picker, admin)
  const [shelters, setShelters] = React.useState([]);
  // Shelter ids with an unverified delivery address (orange "pending" badge on the list).
  const [pendingShelterIds, setPendingShelterIds] = React.useState(function () {
    return new Set();
  });
  const [caseworkers, setCaseworkers] = React.useState([]);
  const [staffRows, setStaffRows] = React.useState([]);
  // All user_shelters membership rows (user_id + shelter_code) — feeds the
  // last-active-worker check when deactivating a user (Phase 2 lifecycle work).
  const [memberships, setMemberships] = React.useState([]);
  // Phase 4: the open shelter deactivate/reactivate confirm, or null.
  const [confirmShelterAction, setConfirmShelterAction] = React.useState(null);
  const [_users, setUsers] = React.useState([]); // legacy - kept for edit/new functions
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  // Deactivated services are hidden by default (13 Oct 2026); ticking "Show deactivated"
  // brings them back, and a search always includes them so a service can still be found.
  const [showDeactivated, setShowDeactivated] = React.useState(false);
  // db-205: "Only services in a sister group" filter + the sister map (fail-safe — an
  // empty map just means no chips and the filter shows none). Reloads whenever the open
  // service changes: this list stays mounted behind ShelterDetail, so without the key a
  // sister-group edit made on a record would not show here until F5.
  const [onlySisters, setOnlySisters] = React.useState(false);
  const sisterIndex = useSisterIndex(shelters, shelterCode);
  const [shelterPage, setShelterPage] = React.useState(0);
  const [staffPage, setStaffPage] = React.useState(0);
  // Shared per-device rows-per-page (services/pageSize.ts) — used by both lists.
  const [rowsPerPage] = usePageSize();
  const [showStaffComms, setShowStaffComms] = React.useState(false);
  // DFV Services list filters (John, 6 Aug 2026). Customer type filters on the
  // BROAD category (db-124 option_category), not the ~85 raw Unleashed types —
  // same approach as the CRM audience / Feedback / Telemarketing filters.
  const [shelterState, setShelterState] = React.useState("");
  const [shelterCategory, setShelterCategory] = React.useState("");
  // Sales Order Group filter ("" = all groups, NO_SALES_GROUP = services with none set)
  // and the column sort (default: DFV Service Code, A to Z).
  const [shelterGroup, setShelterGroup] = React.useState("");
  const [shelterSort, setShelterSort] = React.useState({ key: "code", dir: "asc" });
  const [typeCategories, setTypeCategories] = React.useState({
    byType: new Map(),
    categories: [],
  });
  React.useEffect(function () {
    let live = true;
    loadCustomerTypeCategories().then(function (r) {
      if (live) setTypeCategories(r);
    });
    return function () {
      live = false;
    };
  }, []);
  React.useEffect(
    function () {
      setShelterPage(0);
      setStaffPage(0);
    },
    [search, showDeactivated, shelterState, shelterCategory, shelterGroup, shelterSort, onlySisters]
  );
  const [activeSection, setActiveSection] = React.useState(sections[0]);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [savedMsg, setSavedMsg] = React.useState("");
  const [showNew, setShowNew] = React.useState(false);
  const [showPrimaryCheck, setShowPrimaryCheck] = React.useState(false);
  // Phase 3: when the New User modal is opened from the shelter detail's Users
  // tab, it creates a CASEWORKER with the shelter locked — regardless of the
  // list's activeSection. Null = the normal tab-bar path.
  const [newUserOverride, setNewUserOverride] = React.useState(null); // { lockShelter } | null
  const [mapsReadyTick, setMapsReadyTick] = React.useState(0);
  // When new shelter modal opens, force a Maps readiness check after a short delay
  React.useEffect(
    function () {
      if (!showNew) return;
      const t = setTimeout(function () {
        setMapsReadyTick(function (n) {
          return n + 1;
        });
      }, 300);
      return function () {
        clearTimeout(t);
      };
    },
    [showNew]
  );
  const [newForm, setNewForm] = React.useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    shelter_name: "",
    shelter_code: "",
    role: "shelter",
    state: "",
    customer_type: "",
    sales_group: "",
    taxable: false,
    stock_held: false,
    phone: "",
    primary_contact_label: "DFV Service Manager",
    postal_line1: "",
    postal_suburb: "",
    postal_postcode: "",
    internal_notes: "",
    delivery_line1: "",
    delivery_line2: "",
    delivery_suburb: "",
    delivery_state: "",
    delivery_postcode: "",
    delivery_instructions: "",
    delivery_address_name: "",
  });
  const [showDeliveryModal, setShowDeliveryModal] = React.useState(null);
  const [viewing, setViewing] = React.useState(null);
  const [deliveryAddrs, setDeliveryAddrs] = React.useState([]);
  const [deliveryLoading, setDeliveryLoading] = React.useState(false);
  const [deliverySaving, setDeliverySaving] = React.useState(false);
  const [editingAddr, setEditingAddr] = React.useState(null); // index or "new"
  const [addrForm, setAddrForm] = React.useState({});
  const [newSaving, setNewSaving] = React.useState(false);
  const [newErr, setNewErr] = React.useState("");
  const [showPass, setShowPass] = React.useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = React.useState(null);
  const [customerTypes, setCustomerTypes] = React.useState([]);
  const [salesGroups, setSalesGroups] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [showAddType, setShowAddType] = React.useState(false);
  const [showAddGroup, setShowAddGroup] = React.useState(false);
  const [newTypeVal, setNewTypeVal] = React.useState("");
  const [newGroupVal, setNewGroupVal] = React.useState("");

  function loadUsers() {
    setLoading(true);
    const p1 = listAllShelters().then(function (rows) {
      if (rows) setShelters(rows);
    });
    // Load the pending-verification shelter set (non-blocking; failure → no badges).
    listShelterIdsWithPendingAddresses().then(function (ids) {
      if (ids) setPendingShelterIds(new Set(ids));
    });
    const p2 = listShelterProfiles();
    const p3 = listStaffProfiles();
    // db-189: include deactivated login-less contacts so the Users tab's "Show
    // Deactivated Contacts" toggle can reveal them (they're hidden by default in
    // ShelterDetail). Every OTHER consumer uses the active-only default.
    const p4 = listAllUserShelters({ includeDeactivated: true });
    Promise.all([p1, p2, p3, p4]).then(function (results) {
      const shelterProfiles = results[1] || [];
      const staffProfiles = results[2] || [];
      const userSheltersRows = results[3] || [];
      // Build lookups: user_id -> array of shelter_codes, and -> PRIMARY code.
      // primaryByUser reads user_shelters.is_primary (the source of truth now that
      // profiles.shelter_code is retired — Phase 4 B4-B), falling back to the first
      // membership when none is flagged.
      const usByUser = {};
      const primaryByUser = {};
      const primaryIdByUser = {};
      userSheltersRows.forEach(function (row) {
        if (!usByUser[row.user_id]) usByUser[row.user_id] = [];
        usByUser[row.user_id].push(row.shelter_code);
        if (row.is_primary || !primaryByUser[row.user_id]) {
          primaryByUser[row.user_id] = row.shelter_code;
          primaryIdByUser[row.user_id] = row.shelter_id;
        }
      });
      // Attach memberships + the derived primary shelter code/id to each profile
      shelterProfiles.forEach(function (p) {
        if (usByUser[p.id]) p.shelter_codes = usByUser[p.id];
        if (primaryByUser[p.id]) p.primaryShelterCode = primaryByUser[p.id];
        if (primaryIdByUser[p.id]) p.primaryShelterId = primaryIdByUser[p.id];
      });
      staffProfiles.forEach(function (p) {
        if (usByUser[p.id]) p.shelter_codes = usByUser[p.id];
        if (primaryByUser[p.id]) p.primaryShelterCode = primaryByUser[p.id];
        if (primaryIdByUser[p.id]) p.primaryShelterId = primaryIdByUser[p.id];
      });
      setCaseworkers(shelterProfiles);
      setUsers(shelterProfiles);
      setStaffRows(staffProfiles);
      setMemberships(userSheltersRows);
      setLoading(false);
    });
  }

  function loadLookups() {
    listLookupValues("customer_type").then(function (vals) {
      if (vals) setCustomerTypes(vals);
    });
    listLookupValues("sales_group").then(function (vals) {
      if (vals) setSalesGroups(vals);
    });
    // Warehouse dropdown STORES the Unleashed code (external_ref) but SHOWS the
    // name — so options are { value: code, label: name } (db-58 design change).
    listLookupOptions("warehouse").then(function (opts) {
      if (opts)
        setWarehouses(
          opts.map(function (o) {
            return { value: o.external_ref || o.value, label: o.value };
          })
        );
    });
  }

  React.useEffect(function () {
    loadUsers();
    loadLookups();
  }, []);

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  function fv(f, key) {
    return f[key] !== undefined ? f[key] : "";
  }

  // startEdit — table is "shelters" or "profiles"; shelterId (contact-model 3c-iii,
  // GOTCHA 2) is the service whose Users tab opened the edit — so the per-service
  // Contact Role + the contact fields seed from, and save to, that membership. The
  // Zephyr-Staff editor passes no shelterId (staff have no memberships / no contact).
  function startEdit(u, table, editShelterId) {
    // Contact-model No-Login: a login-less contact (synthetic row, u.noLogin) has no
    // profile — edit only its membership. table:"nologin" routes saveEdit to the
    // membership-only path (+ the create-login transition when No Login is un-ticked).
    if (u.noLogin) {
      const nm = (memberships || []).find(function (m) {
        return m.id === u.membershipId;
      });
      setEditing({
        table: "nologin",
        membershipId: u.membershipId,
        shelterId: editShelterId || undefined,
        originalEmail: nm?.email || "",
        // Prior name — so a person-level fan-out matches the same person, never a
        // stranger sharing a mailbox (contact-fan-out-shared-mailbox-fix-plan).
        originalFirstName: nm?.first_name || "",
        originalLastName: nm?.last_name || "",
      });
      setForm({
        first_name: nm?.first_name || "",
        last_name: nm?.last_name || "",
        user_email: nm?.email || "",
        phone: nm?.phone || "",
        mobile: nm?.mobile || "",
        contact_label: nm?.contact_label || "",
        role: "shelter",
        no_login: true,
      });
      return;
    }
    // The membership CONTACT for the edited service (contact-model Phase 3, Option A):
    // Phone / Mobile / Contact Role seed from user_shelters, NOT from the profile.
    const editMembership = editShelterId
      ? (memberships || []).find(function (m) {
          return m.user_id === u.id && m.shelter_id === editShelterId;
        })
      : null;
    // THE WALL: for a CASEWORKER the login email is also their contact email and
    // lives on the membership; profiles.staff_user_email is Zephyr staff only. A
    // staff account has no membership, so it correctly falls back to staff_*.
    // (Missing this left the Login Email box BLANK for a caseworker created after
    // the Edge Function stopped writing staff_user_email — 6 Aug 2026.)
    const contactEmailRow =
      editMembership ||
      (u.role === ROLES.SHELTER
        ? (memberships || []).find(function (m) {
            return m.user_id === u.id && m.email;
          })
        : null);
    const seededEmail = contactEmailRow?.email || u.staff_user_email || "";
    // originalEmail is captured here so the save flow can detect a login-email
    // change and route it through the admin-users Edge Function (which keeps
    // auth.users.email and the profile/membership copies in sync). It MUST use the
    // same source as the form field below, or every save would look like a change.
    setEditing({
      id: u.id,
      table: table || "profiles",
      originalEmail: seededEmail,
      shelterId: editShelterId || undefined,
    });
    // THE WALL (docs/staff-caseworker-separation-plan.md): a DFV Service
    // caseworker's NAME lives on their membership contact, never on
    // profiles.staff_*, which is Zephyr staff only. Prefer the edited service's
    // membership; fall back to ANY of their memberships, because the person-level
    // name is the same across all of them by design (the edit fans it out) — that
    // covers a caseworker opened without a service context. A Zephyr staff account
    // has no membership at all, so it correctly still reads staff_*.
    const contactNameRow =
      editMembership ||
      (u.role === ROLES.SHELTER
        ? (memberships || []).find(function (m) {
            return m.user_id === u.id && (m.first_name || m.last_name);
          })
        : null);
    setForm({
      first_name: contactNameRow ? contactNameRow.first_name || "" : u.staff_first_name || "",
      last_name: contactNameRow ? contactNameRow.last_name || "" : u.staff_last_name || "",
      contact_label:
        editMembership?.contact_label || (u.role === ROLES.SHELTER ? "DFV Service Manager" : ""),
      phone: editMembership?.phone || "",
      mobile: editMembership?.mobile || "",
      display_name: u.display_name || "",
      shelter_name: u.shelter_name || "",
      // Primary picker seeds from the is_primary membership (u.primaryShelterCode)
      // for a profile edit; a shelter edit has no primaryShelterCode and falls back
      // to the shelter's own code (Phase 4 B4-B).
      shelter_code: (u.primaryShelterCode ?? u.shelter_code) || "",
      role: u.role || "shelter",
      login_method: u.login_method || "password",
      // Staff-only fields (Zephyr Staff editor): mobile, calendar colour, and
      // whether the person appears on the Resources → Staff List directory.
      staff_mobile: u.staff_mobile || "",
      staff_colour: u.staff_colour || "",
      // "Display Name" (db-184) — the short public name shown to shelters ("John W (Zephyr)").
      preferred_name: u.preferred_name || "",
      show_on_staff_list: u.show_on_staff_list !== false,
      // Consultant = a Zephyr person on a non-Zephyr email domain (db-139).
      is_consultant: u.is_consultant === true,
      shelter_codes: Array.isArray(u.shelter_codes)
        ? u.shelter_codes
        : (u.primaryShelterCode ?? u.shelter_code)
          ? [u.primaryShelterCode ?? u.shelter_code]
          : [],
      state: u.state || "",
      // Same source as editing.originalEmail above — see the wall comment there.
      user_email: seededEmail,
      // Contact-model No-Login: a login user starts un-ticked; ticking + Save removes
      // their login (converts them to contact-only). See saveEdit's profiles branch.
      no_login: false,
      customer_type: u.customer_type || "",
      taxable: u.taxable === true || u.taxable === null,
      sales_group: u.sales_group || "",
      postal_line1: u.postal_line1 || "",
      postal_line2: u.postal_line2 || "",
      postal_suburb: u.postal_suburb || "",
      postal_suburb_city: u.postal_suburb_city || "",
      postal_state: u.postal_state || "",
      postal_postcode: u.postal_postcode || "",
      delivery_instructions: u.delivery_instructions || "",
      unleashed_guid: u.unleashed_guid || "",
      internal_notes: u.internal_notes || "",
      date_onboarded: u.date_onboarded || "",
      is_generic: u.is_generic || false,
    });
    // Pre-load delivery addresses for inline preview. The inline preview
    // hides soft-deleted rows — admin sees + manages those inside the
    // dedicated Manage Addresses modal (openDeliveryModal, which loads all).
    const shelterId = u.primaryShelterId ?? (u.shelter_code ? u.id : null);
    if (shelterId) {
      setDeliveryLoading(true);
      listActiveByShelterId(shelterId).then(function (rows) {
        setDeliveryAddrs(rows || []);
        setDeliveryLoading(false);
      });
    } else {
      setDeliveryAddrs([]);
    }
  }

  function setF(key, val) {
    setForm(function (f) {
      return Object.assign({}, f, { [key]: val });
    });
  }

  // Contact-model 3c-iii: the free-form phone list (usePhoneList) is retired from
  // the caseworker edit form — it now has fixed Phone + Mobile inputs on the
  // membership contact. No phone-list helpers needed here anymore.

  // Delivery-address modal handlers — extracted to
  // src/hooks/useUserAdminHandlers.js (Phase A). UserAdmin still owns the
  // state; the hook receives values + setters and returns the closures.
  const _adminHandlers = useUserAdminHandlers({
    user: user,
    addrForm: addrForm,
    showDeliveryModal: showDeliveryModal,
    editingAddr: editingAddr,
    deliveryAddrs: deliveryAddrs,
    setShowDeliveryModal: setShowDeliveryModal,
    setDeliveryLoading: setDeliveryLoading,
    setEditingAddr: setEditingAddr,
    setDeliveryAddrs: setDeliveryAddrs,
    setAddrForm: setAddrForm,
    setDeliverySaving: setDeliverySaving,
    showSaved: showSaved,
    form: form,
    editing: editing,
    newForm: newForm,
    activeSection: activeSection,
    setSaving: setSaving,
    setEditing: setEditing,
    setNewErr: setNewErr,
    setNewSaving: setNewSaving,
    setShowNew: setShowNew,
    setNewForm: setNewForm,
    setConfirmDeactivate: setConfirmDeactivate,
    loadUsers: loadUsers,
  });
  const openDeliveryModal = _adminHandlers.openDeliveryModal;
  const attachUserToShelter = _adminHandlers.attachUserToShelter;
  const detachUserFromShelter = _adminHandlers.detachUserFromShelter;
  const removeNoLoginContact = _adminHandlers.removeNoLoginContact;
  const reactivateNoLoginContact = _adminHandlers.reactivateNoLoginContact;
  const startNewAddr = _adminHandlers.startNewAddr;
  const startEditAddr = _adminHandlers.startEditAddr;
  const saveAddr = _adminHandlers.saveAddr;
  const reactivateAddr = _adminHandlers.reactivateAddr;
  const promoteAddr = _adminHandlers.promoteAddr;
  const setAddrBanned = _adminHandlers.setAddrBanned;
  const setAddrSilent = _adminHandlers.setAddrSilent;
  const deleteAddr = _adminHandlers.deleteAddr;
  const setDefault = _adminHandlers.setDefault;
  const saveEdit = _adminHandlers.saveEdit;
  // The Zephyr Staff list saves through its OWN handler — staff and DFV Service
  // caseworkers must not share a save path (docs/staff-caseworker-separation-plan.md).
  const saveStaffEdit = _adminHandlers.saveStaffEdit;
  const sendPasswordReset = _adminHandlers.sendPasswordReset;
  const resetUserMfa = _adminHandlers.resetUserMfa;
  const createUser = _adminHandlers.createUser;
  const deactivateUser = _adminHandlers.deactivateUser;
  const deactivateShelters = _adminHandlers.deactivateShelters;
  const deactivateShelterCascade = _adminHandlers.deactivateShelterCascade;
  const reactivateShelter = _adminHandlers.reactivateShelter;

  // Phase 4 (docs/account-shelter-lifecycle-plan.md): open the shelter
  // deactivate/reactivate confirm. For a deactivation, compute the active workers
  // that will cascade-deactivate + the in-flight request count (informational).
  async function onDeactivateShelter(s) {
    // Exclusive workers cascade-deactivate (full account); shared workers (also in
    // another shelter) are detached from this shelter only, keeping their account.
    const allProfiles = caseworkers.concat(staffRows);
    const workers = cascadeWorkersForShelter(s.shelter_code, memberships, allProfiles);
    // Tag each shared worker with their profile's PRIMARY shelter_code so the
    // cascade can repoint it if this shelter happened to be their primary.
    const sharedWorkers = sharedWorkersForShelter(s.shelter_code, memberships, allProfiles).map(
      function (w) {
        const prof = allProfiles.find(function (p) {
          return p.id === w.id;
        });
        return {
          ...w,
          profileShelterCode: prof ? (prof.primaryShelterCode ?? prof.shelter_code) : null,
        };
      }
    );
    const inflightCount = await countInflightRequestsByShelterId(s.id);
    setConfirmShelterAction({
      shelter: s,
      mode: "deactivate",
      workers,
      sharedWorkers,
      inflightCount,
    });
  }
  function onReactivateShelter(s) {
    setConfirmShelterAction({ shelter: s, mode: "reactivate" });
  }

  // shelter_code → attached users (any role, incl. deactivated), for the shelter
  // detail "Attached users" list (Phase 4).
  const attachedUsersMap = attachedUsersByShelter(memberships, caseworkers.concat(staffRows));

  // Phase 2 (docs/account-shelter-lifecycle-plan.md): when the deactivate-user
  // dialog is open for a DEACTIVATION, find the shelters that would be left with
  // no active worker — resolved to { id, shelter_code, shelter_name } for the
  // warning and the optional "also deactivate" offer.
  const confirmDeactivateOrphans =
    confirmDeactivate && !confirmDeactivate.deactivated
      ? assessUserDeactivation(
          confirmDeactivate.id,
          memberships,
          caseworkers.concat(staffRows)
        ).orphanedShelters.map(function (code) {
          const s = shelters.find(function (sh) {
            return sh.shelter_code === code;
          });
          return {
            id: s ? s.id : null,
            shelter_code: code,
            shelter_name: s ? s.shelter_name : code,
          };
        })
      : [];

  function addLookupValue(table, val, _setter, setShow, setVal) {
    if (!val.trim()) return;
    insertLookupValue(table, val.trim()).then(function () {
      loadLookups();
      setShow(false);
      setVal("");
      showSaved("Added ✓");
    });
  }

  // ── filtered sets for each section ─────────────────────────────────────
  const q = search.toLowerCase();
  // Phase 3: the Shelters search also matches attached-user name/email, so with
  // no global Caseworkers roster you can still find a caseworker's shelter(s).
  // Sorted by DFV Service Code (the repo returns them name-ordered; the code is
  // what people scan for on this screen).
  // TEST services are left out of the list, its counts and the CSV (13 Oct 2026).
  const listShelters = shelters.filter(function (s) {
    return !isTestShelter(s);
  });
  const sistersText = function (s) {
    return (sisterIndex?.get(s.id)?.sisters || [])
      .map(function (x) {
        return x.code;
      })
      .join(" ");
  };
  const filteredShelters = sortShelters(
    listShelters.filter(function (s) {
      if (!shelterPassesDeactivatedFilter(s, showDeactivated, q)) return false;
      if (onlySisters && !hasSisters(sisterIndex, s.id)) return false;
      if (
        !shelterPassesListFilters(
          s,
          { state: shelterState, category: shelterCategory, group: shelterGroup },
          typeCategories.byType
        )
      )
        return false;
      return shelterMatchesQuery(s, q, attachedUsersMap.get(s.shelter_code));
    }),
    shelterSort,
    sistersText
  );
  const shelterStates = statesFromShelters(listShelters);
  const shelterGroups = salesGroupsFromShelters(listShelters);
  function sortShelterList(key) {
    setShelterSort(function (cur) {
      return cur.key === key
        ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" };
    });
  }
  function downloadShelterCsv() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    downloadCsv(shelterListCsvTable(filteredShelters, sistersText), `dfv-services-${stamp}.csv`);
  }
  const shownLabel =
    shelterGroup === ""
      ? `${filteredShelters.length} shown`
      : `${filteredShelters.length} shown in ${shelterGroup === NO_SALES_GROUP ? "(none set)" : shelterGroup}`;
  // Paginate the DFV Services list (20/page) — it grows large after the Unleashed
  // shelter import. Page resets to 1 when the search / hide-inactive filter changes.
  const SHELTER_PAGE_SIZE = rowsPerPage;
  const shelterTotalPages = Math.max(1, Math.ceil(filteredShelters.length / SHELTER_PAGE_SIZE));
  const shelterSafePage = Math.min(shelterPage, shelterTotalPages - 1);
  const pagedShelters = filteredShelters.slice(
    shelterSafePage * SHELTER_PAGE_SIZE,
    (shelterSafePage + 1) * SHELTER_PAGE_SIZE
  );
  // (filteredCaseworkers is gone — the Caseworkers tab was retired in Phase 3;
  // caseworkers are managed on the shelter detail's Users tab, and the Shelters
  // search above surfaces them by name/email.)
  const filteredStaff = staffRows.filter(function (u) {
    const name = (
      (u.staff_first_name || "") +
      " " +
      (u.staff_last_name || "") +
      " " +
      (u.display_name || "")
    ).toLowerCase();
    return (
      !q ||
      name.includes(q) ||
      (u.staff_user_email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  });
  const STAFF_PAGE_SIZE = rowsPerPage;
  const staffTotalPages = Math.max(1, Math.ceil(filteredStaff.length / STAFF_PAGE_SIZE));
  const staffSafePage = Math.min(staffPage, staffTotalPages - 1);
  const pagedStaff = filteredStaff.slice(
    staffSafePage * STAFF_PAGE_SIZE,
    (staffSafePage + 1) * STAFF_PAGE_SIZE
  );

  // ── Full-screen shelter detail (/users/:shelterCode) ──────────────────────
  // Phase 1 of the tabbed redesign: a shelterCode in the URL replaces the whole
  // list screen with ShelterDetail. The list state (search, active tab) is
  // preserved because this same component stays mounted across both URLs.
  const inShelterDetail = Boolean(shelterCode && sections.includes("shelters"));
  const detailShelter = inShelterDetail
    ? shelters.find(function (s) {
        return s.shelter_code === shelterCode;
      })
    : null;
  // FULL profile rows attached to the detail shelter (Phase 3): the Users tab's
  // Edit needs every profile field (shelter_codes, is_generic, phones, ...), so
  // it gets the raw rows, not the AttachedUser summaries the search uses.
  const detailUserIds = detailShelter
    ? new Set(
        memberships
          .filter(function (m) {
            return m.shelter_code === detailShelter.shelter_code;
          })
          .map(function (m) {
            return m.user_id;
          })
      )
    : null;
  // Contact-model No-Login: login-less contacts (memberships with NULL user_id) have
  // no profile, so they don't appear via the profile join above. Surface them as
  // synthetic "contact-only" rows carrying a `noLogin` flag + their membership id, so
  // the Users tab can show them, make them Primary, and (2b) edit/create/remove them.
  const detailNoLoginRows = detailShelter
    ? memberships
        .filter(function (m) {
          return m.shelter_code === detailShelter.shelter_code && !m.user_id;
        })
        .map(function (m) {
          const nm = [m.first_name, m.last_name].filter(Boolean).join(" ");
          return {
            id: `nologin:${m.id}`,
            noLogin: true,
            membershipId: m.id,
            user_id: null,
            role: "shelter",
            staff_first_name: m.first_name,
            staff_last_name: m.last_name,
            staff_user_email: m.email,
            display_name: nm || m.email || "(no-login contact)",
            deactivated: !!m.deactivated, // db-189: real soft-delete state drives the row
          };
        })
    : [];
  const detailUsers = (
    detailUserIds
      ? caseworkers.concat(staffRows).filter(function (p) {
          return detailUserIds.has(p.id);
        })
      : []
  ).concat(detailNoLoginRows);

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      {inShelterDetail && (
        <div>
          {savedMsg && (
            <div className="text-[13px] text-[#2E7D32] font-semibold mb-2">{savedMsg}</div>
          )}
          {/* Order matters: while a background loadUsers refresh is in flight
              (Save / Primary change), detailShelter stays resolvable, so the
              detail STAYS MOUNTED — unmounting it would bounce the admin back
              to the Details tab and drop unsaved edits. Loading only shows on
              the initial fetch, when the shelters list is still empty. */}
          {detailShelter ? (
            // ⚠️ LEGACY PARALLEL WIRING (John, 9 Aug 2026). This assembles the DFV-Service
            // editor's props inline here and hands them to ShelterEditor's PASS-THROUGH
            // path. ShelterEditor ALSO contains a self-sufficient copy of this wiring, used
            // by the telemarketing call modal and by any NEW module. So there are two copies
            // of the *wiring* (both render the one ShelterDetail). Converging this admin route
            // onto the self-sufficient path ("Part D") is DEFERRED BY DESIGN — a large,
            // hard-to-test refactor of the live admin console for a code-tidiness gain, riskier
            // now that we're live than the (bounded, non-growing) duplication.
            // RULES: (1) a NEW screen/module that edits a DFV Service must mount
            // <ShelterEditor shelterId=… context=… onClose=… /> — do NOT copy this block.
            // (2) If you add a prop to ShelterDetail, wire it HERE *and* in ShelterEditor.
            // See docs/telemarketing-service-editor-plan.md §Part D.
            <ShelterEditor
              context="admin"
              shelter={detailShelter}
              customerTypes={customerTypes}
              salesGroups={salesGroups}
              warehouses={warehouses}
              attachedUsers={detailUsers}
              memberships={memberships}
              deliveryModalShelter={showDeliveryModal}
              onBack={function () {
                navigate("/users");
              }}
              onDeactivateShelter={onDeactivateShelter}
              onReactivateShelter={onReactivateShelter}
              openDeliveryModal={openDeliveryModal}
              deliveryAddrs={deliveryAddrs}
              deliveryLoading={deliveryLoading}
              editingAddr={editingAddr}
              setEditingAddr={setEditingAddr}
              addrForm={addrForm}
              setAddrForm={setAddrForm}
              deliverySaving={deliverySaving}
              saveAddr={saveAddr}
              startNewAddr={startNewAddr}
              startEditAddr={startEditAddr}
              promoteAddr={promoteAddr}
              setAddrBanned={setAddrBanned}
              setAddrSilent={setAddrSilent}
              deleteAddr={deleteAddr}
              reactivateAddr={reactivateAddr}
              setDefault={setDefault}
              usersAdmin={{
                editing: editing,
                form: form,
                setForm: setForm,
                fv: fv,
                setF: setF,
                saving: saving,
                saveEdit: saveEdit,
                startEdit: startEdit,
                cancelEdit: function () {
                  setEditing(null);
                },
                sendPasswordReset: sendPasswordReset,
                setConfirmDeactivate: setConfirmDeactivate,
                shelters: shelters,
                caseworkers: caseworkers,
                onNewUser: function () {
                  setNewForm(function (f) {
                    return Object.assign({}, f, {
                      shelter_code: detailShelter.shelter_code || "",
                      role: "shelter",
                      // Start each "+ New User" open with the contact-only tick cleared.
                      no_login: false,
                    });
                  });
                  setNewUserOverride({ lockShelter: detailShelter.shelter_code || "" });
                  setShowNew(true);
                },
                onAttachUser: function (userId) {
                  // THE WALL: the email handed to attachUserToShelter is a CONTACT
                  // email (it seeds the new membership when there's nothing to copy
                  // from), so take it from an existing membership — not from
                  // profiles.staff_user_email, which is Zephyr staff only.
                  const contact = (memberships || []).find(function (m) {
                    return m.user_id === userId && m.email;
                  });
                  attachUserToShelter(userId, detailShelter.shelter_code || "", contact?.email);
                },
                onDetachUser: function (u) {
                  detachUserFromShelter(u, detailShelter.shelter_code || "");
                },
                onRemoveNoLogin: function (u) {
                  removeNoLoginContact(u);
                },
                onReactivateNoLogin: function (u) {
                  reactivateNoLoginContact(u);
                },
              }}
              onSaved={function (msg) {
                showSaved(msg);
                loadUsers();
              }}
              onMembershipsChanged={loadUsers}
            />
          ) : loading ? (
            <div className="text-center text-zephyr-muted p-10">Loading...</div>
          ) : (
            <div className="text-center text-zephyr-muted p-10">
              DFV Service "{shelterCode}" not found.{" "}
              <button
                type="button"
                onClick={function () {
                  navigate("/users");
                }}
                className="bg-transparent border-0 p-0 text-zephyr-teal underline cursor-pointer"
                style={{ fontFamily: "inherit" }}
              >
                ← Back to DFV Services
              </button>
            </div>
          )}
        </div>
      )}
      {!inShelterDetail && (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="m-0 text-[22px] text-zephyr-text">{title}</h1>
            </div>
            <div className="flex gap-[10px] items-center">
              {savedMsg && (
                <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>
              )}
              {activeSection === "shelters" && (
                <button
                  type="button"
                  onClick={function () {
                    setShowPrimaryCheck(true);
                  }}
                  className="bg-white text-zephyr-teal-dark border border-zephyr-teal rounded-[6px] py-[7px] px-[14px] text-[13px] font-semibold cursor-pointer"
                  style={{ fontFamily: "inherit" }}
                >
                  Check Primary Contacts
                </button>
              )}
              {activeSection === "staff" && (
                <button
                  type="button"
                  onClick={function () {
                    setShowStaffComms(true);
                  }}
                  className="bg-zephyr-teal-dark text-white border-0 rounded-[6px] py-[7px] px-[14px] text-[13px] font-semibold cursor-pointer"
                  style={{ fontFamily: "inherit" }}
                >
                  Staff Communication
                </button>
              )}
            </div>
          </div>

          {/* Section tabs + search — extracted to ./UserAdminTabsBar.jsx */}
          <UserAdminTabsBar
            sections={sections}
            shelters={shelters}
            staffRows={staffRows}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            setEditing={setEditing}
            setViewing={setViewing}
            search={search}
            setSearch={setSearch}
            setShowNew={function (v) {
              // Tab-bar opens are never the detail's locked-caseworker context.
              setNewUserOverride(null);
              setShowNew(v);
            }}
          />

          {loading ? (
            <div className="text-center text-zephyr-muted p-10">Loading...</div>
          ) : (
            <div>
              {/* ══ SHELTERS SECTION — extracted to ./UserAdminShelterList.jsx.
              Slimmed Phase 1: rows navigate to the full-screen ShelterDetail
              (/users/:code) instead of expanding inline. */}
              {activeSection === "shelters" && (
                <>
                  {/* Filters: State + Customer type CATEGORY (not the ~85 raw
                      Unleashed types), alongside the existing hide-deactivated. */}
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDeactivated}
                        onChange={function (e) {
                          setShowDeactivated(e.target.checked);
                        }}
                      />
                      Show deactivated DFV Services
                    </label>
                    <label className="flex items-center gap-2 text-[13px] text-zephyr-text cursor-pointer">
                      <input
                        type="checkbox"
                        checked={onlySisters}
                        onChange={function (e) {
                          setOnlySisters(e.target.checked);
                        }}
                      />
                      Only services in a sister group
                    </label>
                    <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
                      State
                      <select
                        value={shelterState}
                        aria-label="Filter by state"
                        onChange={function (e) {
                          setShelterState(e.target.value);
                        }}
                        className="py-[5px] px-[8px] rounded-[7px] border border-zephyr-border text-[13px] bg-white text-zephyr-text"
                      >
                        <option value="">All states</option>
                        {shelterStates.map((st) => (
                          <option key={st} value={st}>
                            {st}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
                      Customer type
                      <select
                        value={shelterCategory}
                        aria-label="Filter by customer type"
                        onChange={function (e) {
                          setShelterCategory(e.target.value);
                        }}
                        className="py-[5px] px-[8px] rounded-[7px] border border-zephyr-border text-[13px] bg-white text-zephyr-text"
                      >
                        <option value="">All customer types</option>
                        {typeCategories.categories.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-[13px] text-zephyr-text">
                      Sales Order Group
                      <select
                        value={shelterGroup}
                        aria-label="Filter by sales order group"
                        onChange={function (e) {
                          setShelterGroup(e.target.value);
                        }}
                        className="py-[5px] px-[8px] rounded-[7px] border border-zephyr-border text-[13px] bg-white text-zephyr-text"
                      >
                        <option value="">All groups</option>
                        {shelterGroups.groups.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                        {shelterGroups.hasNone && (
                          <option value={NO_SALES_GROUP}>(none set)</option>
                        )}
                      </select>
                    </label>
                    <span className="text-[12px] text-zephyr-muted font-semibold">
                      {shownLabel}
                    </span>
                    <button
                      type="button"
                      onClick={downloadShelterCsv}
                      className="bg-white rounded-[6px] py-1 px-[10px] text-[12px] cursor-pointer text-zephyr-teal-dark"
                      style={{ border: "1px solid #00897B", fontFamily: "inherit" }}
                    >
                      ⬇ Download CSV
                    </button>
                  </div>
                  <UserAdminShelterList
                    filteredShelters={pagedShelters}
                    pendingShelterIds={pendingShelterIds}
                    sisterIndex={sisterIndex}
                    sort={shelterSort}
                    onSort={sortShelterList}
                    onOpenShelter={function (s) {
                      navigate(`/users/${encodeURIComponent(s.shelter_code || "")}`);
                    }}
                  />
                  {shelterTotalPages > 1 && (
                    <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
                      <button
                        type="button"
                        onClick={function () {
                          setShelterPage(Math.max(0, shelterSafePage - 1));
                        }}
                        disabled={shelterSafePage === 0}
                        className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Prev
                      </button>
                      {Array.from({ length: shelterTotalPages }, function (_, i) {
                        return i;
                      }).map(function (i) {
                        const active = i === shelterSafePage;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={function () {
                              setShelterPage(i);
                            }}
                            className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
                              active
                                ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                                : "bg-white border-zephyr-border text-zephyr-text"
                            }`}
                          >
                            {i + 1}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={function () {
                          setShelterPage(Math.min(shelterTotalPages - 1, shelterSafePage + 1));
                        }}
                        disabled={shelterSafePage >= shelterTotalPages - 1}
                        className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* (Caseworkers tab RETIRED — Phase 3: caseworkers are managed on
                  the shelter detail's Users tab.) */}

              {/* ══ ZEPHYR STAFF SECTION — extracted to ./UserAdminStaffList.jsx */}
              {activeSection === "staff" && (
                <>
                  <UserAdminStaffList
                    filteredStaff={pagedStaff}
                    editing={editing}
                    setEditing={setEditing}
                    viewing={viewing}
                    setViewing={setViewing}
                    form={form}
                    fv={fv}
                    setF={setF}
                    saving={saving}
                    saveEdit={saveStaffEdit}
                    startEdit={startEdit}
                    sendPasswordReset={sendPasswordReset}
                    resetUserMfa={resetUserMfa}
                    setConfirmDeactivate={setConfirmDeactivate}
                    canManageDocs={isSuperUser(user)}
                  />
                  {staffTotalPages > 1 && (
                    <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
                      <button
                        type="button"
                        onClick={function () {
                          setStaffPage(Math.max(0, staffSafePage - 1));
                        }}
                        disabled={staffSafePage === 0}
                        className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Prev
                      </button>
                      {Array.from({ length: staffTotalPages }, function (_, i) {
                        return i;
                      }).map(function (i) {
                        const active = i === staffSafePage;
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={function () {
                              setStaffPage(i);
                            }}
                            className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
                              active
                                ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                                : "bg-white border-zephyr-border text-zephyr-text"
                            }`}
                          >
                            {i + 1}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={function () {
                          setStaffPage(Math.min(staffTotalPages - 1, staffSafePage + 1));
                        }}
                        disabled={staffSafePage >= staffTotalPages - 1}
                        className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* New DFV Service setup — stepped wizard (shelters kind only). */}
      <NewShelterWizard
        open={showNew && activeSection === "shelters" && !newUserOverride}
        onClose={function () {
          setShowNew(false);
          setNewUserOverride(null);
        }}
        newForm={newForm}
        setNewForm={setNewForm}
        newSaving={newSaving}
        newErr={newErr}
        setNewErr={setNewErr}
        createUser={createUser}
        customerTypes={customerTypes}
        salesGroups={salesGroups}
        warehouses={warehouses}
        shelters={shelters}
        mapsReadyTick={mapsReadyTick}
      />
      {/* New User Modal — caseworker / staff (+ shelter-detail "+ New User"). */}
      <NewUserModal
        open={showNew && !(activeSection === "shelters" && !newUserOverride)}
        onClose={function () {
          setShowNew(false);
          setNewUserOverride(null);
        }}
        kind={newUserOverride ? "caseworkers" : activeSection}
        lockShelter={newUserOverride ? newUserOverride.lockShelter : ""}
        newForm={newForm}
        setNewForm={setNewForm}
        newSaving={newSaving}
        newErr={newErr}
        setNewErr={setNewErr}
        showPass={showPass}
        setShowPass={setShowPass}
        createUser={createUser}
        shelters={shelters}
        customerTypes={customerTypes}
        salesGroups={salesGroups}
        mapsReadyTick={mapsReadyTick}
      />
      {/* Check Primary Contacts — read-only audit of services with no Primary nominated. */}
      <PrimaryContactCheckModal
        open={showPrimaryCheck}
        onClose={function () {
          setShowPrimaryCheck(false);
        }}
        onOpenShelter={function (code) {
          setShowPrimaryCheck(false);
          navigate(`/users/${encodeURIComponent(code || "")}`);
        }}
      />
      {/* Add Customer Type modal — extracted to ./AddLookupModal.jsx */}
      <AddLookupModal
        open={showAddType}
        title="Add Customer Type"
        placeholder="e.g. Non-Profit"
        value={newTypeVal}
        onChange={setNewTypeVal}
        onCancel={function () {
          setShowAddType(false);
          setNewTypeVal("");
        }}
        onAdd={function () {
          addLookupValue(
            "customer_type",
            newTypeVal,
            setCustomerTypes,
            setShowAddType,
            setNewTypeVal
          );
        }}
      />

      {/* Add Sales Group modal — extracted to ./AddLookupModal.jsx */}
      <AddLookupModal
        open={showAddGroup}
        title="Add Sales Group"
        placeholder="e.g. Enterprise"
        value={newGroupVal}
        onChange={setNewGroupVal}
        onCancel={function () {
          setShowAddGroup(false);
          setNewGroupVal("");
        }}
        onAdd={function () {
          addLookupValue(
            "sales_group",
            newGroupVal,
            setSalesGroups,
            setShowAddGroup,
            setNewGroupVal
          );
        }}
      />

      {/* Delivery-address management moved INLINE onto the ShelterDetail Delivery tab
          (8 Aug) — the intermediate Manage-Addresses modal is retired. */}
      {/* Deactivate confirm modal — extracted to ./DeactivateUserConfirmModal.jsx.
          Phase 2: pass the orphaned shelters so the modal can warn + offer to
          deactivate them (docs/account-shelter-lifecycle-plan.md). */}
      <DeactivateUserConfirmModal
        user={confirmDeactivate}
        orphanedShelters={confirmDeactivateOrphans}
        onClose={function () {
          setConfirmDeactivate(null);
        }}
        onConfirm={function (u, opts) {
          deactivateUser(u);
          if (opts?.alsoDeactivateShelters) {
            const toDeactivate = confirmDeactivateOrphans.filter(function (o) {
              return o.id;
            });
            if (toDeactivate.length) deactivateShelters(toDeactivate);
          }
        }}
      />

      {/* Phase 4: shelter deactivate/reactivate confirm (cascade). */}
      <ShelterDeactivateConfirmModal
        action={confirmShelterAction}
        onClose={function () {
          setConfirmShelterAction(null);
        }}
        onConfirm={async function (a) {
          setConfirmShelterAction(null);
          if (a.mode === "deactivate") {
            await deactivateShelterCascade(a.shelter, a.workers, a.sharedWorkers);
          } else {
            await reactivateShelter(a.shelter);
          }
        }}
      />

      {/* The Unleashed Customer sync utilities moved to Manage → DFV Services → Import
          Unleashed Data (John, 3 Aug 2026) so all DFV Services / Unleashed tools live in
          one spot; the "Sync with Unleashed" modal here was retired. */}

      {showStaffComms && (
        <StaffCommsModal
          onClose={function () {
            setShowStaffComms(false);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZephyrStaffAdmin — standalone Manage → Zephyr Staff console (route /staff).
// ─────────────────────────────────────────────────────────────────────────────
// Split out of "Shelters & Users" on 5 July 2026 (shelter-admin redesign
// Phase 1): Zephyr Staff are direct platform users, whereas caseworkers exist
// as part of a shelter. Same console, scoped to the staff section only — all
// state/handlers (create, edit, password/MFA reset, deactivate) are reused.
function ZephyrStaffAdmin() {
  return <UserAdmin sections={["staff"]} title="Zephyr Staff" />;
}

// ─── BANNER DISPLAY (shown to shelter/staff on their dashboard) ───────────────
export { UserAdmin, ZephyrStaffAdmin };
